import axios from 'axios';
import { encodeQueryParams } from '@/utils/queryParams';

// This function is used to extract legacy query params from the query params
// object. It returns a new object with the legacy params and removes them from
// the original object.
// Todo: Remove this function once API is aligned with new query params format.
function extractLegacyParams(queryParams) {
  const legacyParams = {};
  const legacyKeys = ['tag', 'fraudRating'];

  legacyKeys.forEach((key) => {
    if (queryParams && queryParams[key]) {
      legacyParams[key] = queryParams[key];
      delete queryParams[key];
    }
  });

  return legacyParams;
}

export default {
  state: {
    customersByStringifiedQueryParams: {},
    customersByID: {},
    // Everything below here is DEPRECATED. Avoid using if possible so we can
    // remove over time.
    list: [],
    customerBankAccounts: undefined,
    selectedCustomer: undefined,
    customerLoading: true,
    customersLoading: true,
    hasLoadedCustomers: false,
    numberOfCustomerPages: undefined,
    numberOfCustomers: undefined,
    showBlocklistSuggestions: false,
    groupedParsedDetails: undefined,
    getCustomersCancelTokenSource: axios.CancelToken.source(),
    customersProfiles: {},
  },
  mutations: {
    addCustomer(state, customer) {
      if (!customer) {
        return;
      }
      state.list.unshift(customer);
    },
    setCustomers(state, response) {
      if (!response) {
        return;
      }
      state.numberOfCustomerPages =
        response.metadata.pagination.number_of_pages;
      state.numberOfCustomers = response.metadata.pagination.total;
      state.list = response.data;
      state.hasLoadedCustomers = true;
    },
    setCustomersLoading(state, loadingState) {
      state.customersLoading = loadingState;
    },
    setCustomerLoading(state, loadingState) {
      state.customerLoading = loadingState;
    },
    setGetCustomersCancelTokenSource(state, source) {
      state.getCustomersCancelTokenSource = source;
    },
    setShowBlocklistSuggestions(state, source) {
      state.showBlocklistSuggestions = source;
    },
    setGroupedParsedDetails(state, source) {
      state.groupedParsedDetails = source;
    },
    setSelectedCustomerId(state, customerId) {
      state.selectedCustomerId = customerId;
      state.selectedDocumentId = undefined;
      state.documents = [];
    },
    setCustomerProfile(state, args) {
      state.customersProfiles[args.customerId] = args.data;
    },
    setSelectedCustomer(state, customer) {
      if (
        !!state.selectedCustomer &&
        !!customer &&
        state.selectedCustomer.id !== customer.id
      ) {
        state.documents = [];
      }
      state.selectedDocumentId = undefined;
      state.selectedCustomer = customer;
    },
    updateSelectedCustomer(state, customerData = {}) {
      state.selectedCustomer = {
        ...state.selectedCustomer,
        ...customerData,
      };
    },
    setCustomerBankAccounts(state, response) {
      state.customerBankAccounts = response.data;
    },
  },
  actions: {
    async fetchAndCacheCustomers(context, queryParams = {}) {
      const stringifiedParams = encodeQueryParams(queryParams);
      const res = await axios.get(
        `/api/v2/customers?${stringifiedParams}&style=advanced`,
      );
      context.state.customersByStringifiedQueryParams[stringifiedParams] =
        res.data;
      return res.data;
    },
    async fetchAndCacheCustomer(context, customerId) {
      const res = await axios.get(
        `/api/v2/customers/${customerId}?style=advanced`,
      );
      context.state.customersByID[customerId] = res.data;
      return res.data;
    },
    async fetchCustomersRiskCounts() {
      const res = await axios.get(`/api/v2/customers/risk_counts/`);
      return res.data;
    },

    // DEPRECATED below this point
    getCustomerParsedDetails(context, customerId) {
      return axios.get(`/api/v2/customers/${customerId}/parsed-details/`);
    },
    fetchCustomerProfile(context, customerId) {
      return axios
        .get(`/api/v2/agents/canonical-profile?customer_uuid=${customerId}`)
        .then((response) => {
          context.commit('setCustomerProfile', {
            data: response.data?.data,
            customerId,
          });
        });
    },
    getCustomers(context, queryParams) {
      context.commit('setCustomersLoading', true);
      const url = `/api/v2/customers/`;

      context.state.getCustomersCancelTokenSource.cancel();

      const legacyParams = extractLegacyParams(queryParams);
      // Todo: Create proper url builder for this
      if (queryParams?.search) {
        queryParams.search = decodeURIComponent(queryParams.search);
      }
      if (queryParams) {
        queryParams.style = 'advanced';
      } else {
        queryParams = {
          style: 'advanced',
        };
      }

      const source = axios.CancelToken.source();
      const promise = axios
        .get(`${url}?${encodeQueryParams(queryParams)}`, {
          params: legacyParams,
          cancelToken: source.token,
        })
        .then((response) => {
          context.commit('setCustomers', response.data);
          context.commit('setCustomersLoading', false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            throw error;
          }
        });
      context.commit('setGetCustomersCancelTokenSource', source);

      return promise;
    },
    createNewCustomer(context, customer) {
      return axios.post(`/api/v2/customers/`, customer).then((response) => {
        return response;
      });
    },
    updateCustomer(context, customer) {
      return axios
        .put(`/api/v2/customers/${customer.id}/?style=advanced`, customer)
        .then((response) => {
          return response;
        });
    },
    deleteCustomer(context, id) {
      return axios.delete(`/api/v2/customers/${id}`).then((response) => {
        return response;
      });
    },
    createCollectSession(context, collectSession) {
      return axios.post('/api/v2/collect/sessions/', collectSession);
    },
    getCustomerBankAccounts({ commit }, params) {
      const url = `/api/v2/customers/${params.customerId}/bank_accounts/`;
      return axios
        .get(url, { params: params.queryParams })
        .then((response) => {
          commit('setCustomerBankAccounts', response.data);
        })
        .catch((error) => {
          throw error;
        });
    },
    getCustomerFraudRatingHistory(context, customerId) {
      return axios.get(`/api/v2/customers/${customerId}/fraud_rating_history`);
    },
  },
  getters: {
    getCustomerProfile: (state) => (customerId) => {
      return state.customersProfiles[customerId];
    },
    getCachedCustomers:
      (state) =>
      (queryParams = {}) => {
        const stringifiedParams = encodeQueryParams(queryParams);
        const cached =
          state.customersByStringifiedQueryParams[stringifiedParams];
        if (!cached) {
          return undefined;
        }
        return cached;
      },
    getCachedCustomerById: (state) => (customerId) => {
      return state.customersByID[customerId];
    },
  },
};
